"use client";

import { useContactPageRouterContext } from "../../../helpers/ContactPageRouterContext";
import { useLocaleStringContext } from "../../../helpers/LocaleStringContext";
import { analyticsClick } from "../../../helpers/analytics";
import { QuestionDetailModal } from "../QuestionDetailModal/QuestionDetailModal";
import { useRouter } from "next/navigation";

export type OnSelectQuestionProps = {
  questionUuid: string;
  questionName: string;
  questionTopic: string;
};

export const QuestionDetailModalRoot = ({
  closeButtonLabel,
  categoryNameByQuestionUuid,
}: {
  closeButtonLabel: string;
  /**
   * Question UUID → owning category name, precomputed on the server.
   *
   * This is a client component, so anything passed here is serialized into the
   * RSC payload of every support-hub page. It previously took
   * `ISbStoryData<CrhCategory>[]` with all questions resolved and scanned them
   * for a single string; that was ~130 full Storyblok stories on the wire per
   * request. See the support-hub `layout.tsx` for where this is built.
   */
  categoryNameByQuestionUuid: Record<string, string>;
}) => {
  const contactPageRouter = useContactPageRouterContext();
  const localeString = useLocaleStringContext();
  const router = useRouter();

  return (
    // Render unconditionally and let `questionUuid` drive `isOpen` inside the
    // modal. Unmounting while `isOpen` is still true would prevent react-modal
    // from running its close lifecycle (focus restore, transition cleanup).
    <QuestionDetailModal
      questionUuid={contactPageRouter.selectedQuestionUuid}
      localeString={localeString}
      onClose={() => router.push(contactPageRouter.deselectQuestionUrl())}
      onSelectQuestion={({
        questionUuid,
        questionName,
        questionTopic,
      }: OnSelectQuestionProps) => {
        const categoryName = categoryNameByQuestionUuid[questionUuid] ?? null;

        analyticsClick({
          value: `${categoryName} | ${questionName} | ${questionTopic}`,
          clickUrl: null,
          elementPosition: "overlay window",
          method: "Outbound: FALSE",
          contentType: "question",
        });
      }}
      closeButtonLabel={closeButtonLabel}
    />
  );
};
